
import React, { useEffect } from 'react';
import { CloseIcon, CheckmarkCircleIcon, SpinnerIcon } from '@/assets/icons';
;

interface NotificationToastProps {
  id?: number; 
  message: string;
  type: 'success' | 'error' | 'info' | 'loading';
  title: string;
  onClose: () => void;
  duration?: number;
}

export const NotificationToast: React.FC<NotificationToastProps> = ({ message, type, title, onClose, duration = 5000, id }) => {
  useEffect(() => {
    if (type !== 'loading') { // Don't auto-dismiss loading toasts
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [onClose, duration, type, id]);

  const typeConfig = {
    success: {
      bgColor: 'bg-white',
      textColor: 'text-gray-800',
      icon: <CheckmarkCircleIcon className="w-6 h-6 mr-3 text-green-500" />,
      closeIconColor: 'text-gray-400 hover:text-gray-600'
    },
    loading: {
      bgColor: 'bg-white',
      textColor: 'text-gray-800',
      icon: <SpinnerIcon className="w-6 h-6 mr-3 text-primaryGreen" />,
      closeIconColor: 'text-gray-400 hover:text-gray-600'
    },
    error: {
        bgColor: 'bg-white',
        textColor: 'text-gray-800',
        icon: <CheckmarkCircleIcon className="w-6 h-6 mr-3 text-red-500" />, // Placeholder
        closeIconColor: 'text-gray-400 hover:text-gray-600'
    },
    info: {
        bgColor: 'bg-white',
        textColor: 'text-gray-800',
        icon: <CheckmarkCircleIcon className="w-6 h-6 mr-3 text-blue-500" />, // Placeholder
        closeIconColor: 'text-gray-400 hover:text-gray-600'
    }
  };

  const config = typeConfig[type];

  return (
    <div 
      className={`w-full max-w-sm p-4 rounded-lg shadow-lg ${config.bgColor} ${config.textColor} animate-fade-in-up border border-gray-200`}
      role="alert"
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">{config.icon}</div>
        <div className="flex-1 ml-3">
          <p className={`text-sm font-semibold text-darkGreen`}>{title}</p>
          <p className={`text-sm text-gray-600`}>{message}</p>
        </div>
        <button
          onClick={onClose}
          className={`ml-3 p-1 rounded-md ${config.closeIconColor}`}
          aria-label="Close notification"
        >
          <CloseIcon className={`w-5 h-5`} />
        </button>
      </div>
    </div>
  );
};
